import {
  AssistantTypeResponse,
  ChatMessage,
  Conversation,
  ConversationRequest,
  FrontEndSettings,
  JobStatus,
  MetadataFolder,
  MetadataResponse,
  UseCase,
  UseCasesResponse,
} from "./models";

export async function callConversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal,
  useCase: string,
  useInternet: boolean,
  excludedMetadata: string[] = [],
  folderNumber: number,
): Promise<Response> {
  const response = await fetch("/api/conversation", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      messages: options.messages,
      conversation_id: options.id,
      use_case: useCase,
      use_internet: useInternet,
      excluded_metadata: excludedMetadata,
      selected_folders: folderNumber,
    }),
    signal: abortSignal,
  });

  if (!response.ok && response.status !== 202) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }

  return response;
}

export async function getAssistantTypeApi(): Promise<AssistantTypeResponse | null> {
  try {
    const response = await fetch("/api/assistanttype", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status == 403) {
      window.location.href = "/unauthorised";
      return null;
    }

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const config: AssistantTypeResponse = await response.json();
    return config;
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
    return null; // Return null or some default value in case of error
  }
}

export async function getPermissionsForRole(roleId: number): Promise<string[]> {
  try {
    const response = await fetch(`/api/permissions/${roleId}`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.permissions ?? [];
  } catch {
    return [];
  }
}

export async function getUserDg(): Promise<string> {
  const info = await getUserInfo();
  return info.dg;
}

export async function getUserInfo(): Promise<{
  dg: string;
  role?: number;
  canViewMultipleDg?: boolean;
  displayName?: string;
}> {
  try {
    const response = await fetch("/api/user/dg", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch user info:", response.statusText);
      return { dg: "INTPA", role: undefined, canViewMultipleDg: false };
    }

    const data = await response.json();
    const role = typeof data.role === "number" ? data.role : undefined;
    return {
      dg: data.dg || "INTPA",
      role,
      canViewMultipleDg: data.canViewMultipleDg === true,
      displayName:
        typeof data.display_name === "string" ? data.display_name : undefined,
    };
  } catch (error) {
    console.error("Failed to fetch user info:", error);
    return { dg: "INTPA", role: undefined, canViewMultipleDg: false };
  }
}

export async function switchDg(dg: string): Promise<{ dg: string }> {
  const response = await fetch("/api/user/dg", {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ dg }),
  });

  if (!response.ok) {
    throw new Error(`Failed to switch DG: ${response.statusText}`);
  }

  return response.json();
}

export async function getDgEnvironments(): Promise<{
  rows: { dg: string; name: string; display_name?: string }[];
}> {
  try {
    const response = await fetch("/api/dgs", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return { rows: [] };
    }

    return response.json();
  } catch {
    return { rows: [] };
  }
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  try {
    const response = await fetch("/api/history/read", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    const messages: ChatMessage[] = [];

    if (payload?.messages) {
      payload.messages.forEach((msg: any) => {
        const message: ChatMessage = {
          id: msg.id,
          role: msg.role,
          date: msg.createdAt,
          content: msg.content,
          intent: msg.intent ?? "",
          feedback: msg.feedback ?? undefined,
        };
        messages.push(message);
      });
    }

    return messages;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return [];
  }
};

export interface ConversationListResponse {
  conversations: Conversation[];
  totalCount: number;
}

export const historyList = async (
  offset = 0,
): Promise<ConversationListResponse | null> => {
  try {
    const response = await fetch(`/api/history/list?offset=${offset}`, {
      method: "GET",
      credentials: "include",
    });

    const payload = await response.json();

    if (!payload || !Array.isArray(payload.conversations)) {
      console.error("There was an issue fetching your data.");
      return null;
    }

    return mapConversationListResponse(payload);
  } catch (err) {
    console.error("There was an issue fetching your data.", err);
    return null;
  }
};

const mapConversationListResponse = (payload: {
  conversations: any[];
  totalCount?: number;
}): ConversationListResponse => {
  const conversations: Conversation[] = payload.conversations.map(
    (conv: any) => ({
      id: conv.id,
      title: conv.title,
      date: conv.createdAt,
      updatedAt: conv?.updatedAt,
      useCase: conv?.useCase,
      messages: [],
    }),
  );

  return {
    conversations,
    totalCount: payload.totalCount ?? conversations.length,
  };
};

export const historySearch = async (
  query: string,
  offset = 0,
): Promise<ConversationListResponse | null> => {
  try {
    const params = new URLSearchParams({
      q: query,
      offset: String(offset),
    });
    const response = await fetch(`/api/history/search?${params.toString()}`, {
      method: "GET",
      credentials: "include",
    });

    const payload = await response.json();

    if (!response.ok) {
      console.error("There was an issue searching chat history.", payload);
      return null;
    }

    if (!payload || !Array.isArray(payload.conversations)) {
      console.error("There was an issue searching chat history.");
      return null;
    }

    return mapConversationListResponse(payload);
  } catch (err) {
    console.error("There was an issue searching chat history.", err);
    return null;
  }
};

export const historyUpdate = async (
  messages: ChatMessage[],
  convId: string,
  useCase: string,
): Promise<Response> => {
  try {
    const payload = {
      conversation_id: convId,
      messages: messages,
      use_case: useCase,
    };

    const response = await fetch("/api/history/update", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyRename = async (
  convId: string,
  title: string,
): Promise<Response> => {
  try {
    const response = await fetch("/api/history/rename", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
        title: title,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyDelete = async (convId: string): Promise<Response> => {
  try {
    const response = await fetch("/api/history/delete", {
      method: "DELETE",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyDeleteAll = async (): Promise<Response> => {
  try {
    const response = await fetch("api/history/delete_all", {
      method: "DELETE",
      credentials: "include",
      body: JSON.stringify({}),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const uploadDocumentsApi = async (
  files: File[],
  conversationId: string,
  useCase: string,
): Promise<void> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  formData.append("conversation_id", conversationId);
  formData.append("use_case", useCase);

  const response = await fetch("/api/upload", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }
};

export interface ConversationDocument {
  name: string;
  creation_time: string;
}

export const getConversationDocumentsApi = async (
  conversationId: string,
): Promise<ConversationDocument[]> => {
  const formData = new FormData();
  formData.append("conversation_id", conversationId);

  const response = await fetch("/api/documents_by_conversation_id", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }
  return response.json();
};

export async function getFrontEndSettings(): Promise<FrontEndSettings> {
  try {
    const response = await fetch("/api/history/frontend_settings", {
      method: "GET",
      credentials: "include",
    });
    if (response.status == 401) {
      throw new Error("Authentication Required");
    }
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const responseJSON = await response.json();
    return responseJSON;
  } catch (error) {
    console.error("Failed to fetch Front End Settings:", error);
    return { CHAT_HISTORY_ENABLED: false, error };
  }
}

export const getMetadataFolders = async (): Promise<MetadataFolder[]> => {
  try {
    const response = await fetch("/api/get_metadata_folders", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication Required");
      }
      throw new Error("Network response was not ok");
    }

    const data: MetadataResponse = await response.json();
    return data.folders || [];
  } catch (error) {
    console.error("Failed to fetch metadata folders:", error);
    return [];
  }
};

export async function pollJobStatus(
  jobId: string,
  onComplete: (result: JobStatus) => void,
  onError: (error: string) => void,
  onProgress?: (job: JobStatus) => void,
  intervalMs = 5000,
  maxAttempts = 180,
): Promise<void> {
  let attempts = 0;

  const poll = async () => {
    attempts++;
    try {
      const response = await fetch(`/api/conversation/status/${jobId}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        onError(`Failed to fetch job status: ${response.status}`);
        return;
      }

      const job: JobStatus = await response.json();

      if (job.status === "queued" || job.status === "processing") {
        onProgress?.(job);
      }

      if (job.status === "completed") {
        onComplete(job);
        return;
      }

      if (job.status === "failed") {
        onError(job.error ?? "Job failed");
        return;
      }

      if (attempts >= maxAttempts) {
        onError("Job timed out waiting for completion");
        return;
      }

      setTimeout(poll, intervalMs);
    } catch (err) {
      onError(`Polling error: ${err}`);
    }
  };

  setTimeout(poll, intervalMs);
}

let _useCasesPromise: Promise<UseCase[]> | null = null;

export function invalidateUseCasesCache(): void {
  _useCasesPromise = null;
}

export const getUseCases = async (): Promise<UseCase[]> => {
  if (_useCasesPromise) return _useCasesPromise;

  _useCasesPromise = (async () => {
    try {
      const response: Response = await fetch("/api/get_use_cases", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication Required");
        }
        throw new Error("Network response was not ok");
      }

      const useCases: UseCase[] = await response.json().then((data) => {
        return data.use_cases.map((uc: any) => ({
          id: uc.id,
          name: uc.name,
          description: uc.description,
          requirements: uc.requirements,
          confirmationMessage: uc.confirmation_message,
          isDefaultForDG: uc.isDefaultForDG === true,
          icon: uc.icon,
          availableFunctions: uc.available_functions || [],
          virtualLibraryDropdownLabel: uc.virtual_library_dropdown_label || "",
          virtualLibraryAvailableFolders:
            uc.virtual_library_available_folders || [],
        }));
      });
      return useCases;
    } catch (error) {
      _useCasesPromise = null; // allow retry on failure
      console.error("Failed to fetch use cases:", error);
      return [];
    }
  })();

  return _useCasesPromise;
};
